import { useState } from 'react';
import { Link } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ActionableNotification, Button, InlineNotification, Modal, NumberInput, RadioButton, RadioButtonGroup, Tag, TextArea, TextInput, Toggle,
} from '@carbon/react';
import { formatVnDateTime, fromZoneInput, toZoneInput } from '@ruby-cms/shared';
import { api, ApiError } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { BlockSkeleton, ErrorState, InlineError, PageHeader } from '../components/states';
import { When } from '../components/format';
import { CommandStatus, HE_A_ERRORS } from '../components/labels';
import type { Command } from './Commands';

// Cong tac he thong (P1-D04, backend/lib/cong_tac.js): bao tri toan trang va bat / tat dang ky, nap,
// rut, giftcode, chat, chuyen tien dai ly tung chieu (docs/specs/cong-tac-chuyen-tien.md). He A va he B doc lai o moi
// lan dung; trang nay chi ghi file `cong_tac`.
const FEATURES = [
  ['dangKy', 'Đăng ký', 'Tạo tài khoản mới'],
  ['nap', 'Nạp tiền', 'Tạo lệnh nạp'],
  ['rut', 'Rút tiền', 'Tạo lệnh rút'],
  ['giftcode', 'Giftcode', 'Nhập giftcode ở sảnh'],
  ['chat', 'Chat', 'Mọi kênh chat trong game'],
  ['chuyenDlTv', 'Đại lý → thành viên', 'Chuyển tiền đại lý cho thành viên (cổng đại lý và trong game)'],
  ['chuyenTvDl', 'Thành viên → đại lý', 'Chuyển tiền thành viên cho đại lý (tab Chuyển tiền trong game)'],
] as const;
type FeatureKey = (typeof FEATURES)[number][0];
type Feature = { bat?: boolean; thongBao?: string };
type BaoTri = { bat?: boolean; thongBao?: string; tu?: string; den?: string; vanDangMo?: 'chot' | 'hoan'; baoTruocPhut?: number };
type Doc = { baoTri?: BaoTri } & Partial<Record<FeatureKey, Feature>>;
type Data = {
  doc: Doc; effective: (Doc & { giayKiem?: number; vanKetGiay?: number; hoatDongGiay?: number }) | null; maintenanceNow: boolean;
  version: number; at: string | null; pending: { id: string; status: string; createdAt: string; lastError: string | null }[];
};
type Form = {
  baoTri: { bat: boolean; thongBao: string; tu: string; den: string; vanDangMo: 'chot' | 'hoan'; baoTruocPhut: number };
} & Record<FeatureKey, { bat: boolean; thongBao: string }>;

// datetime-local <-> ISO (the operator types the time of the CMS time zone)
const toLocal = (iso?: string) => {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : toZoneInput(d);
};
const fromLocal = (v: string) => (v ? fromZoneInput(v)?.toISOString() : undefined);

const toForm = (e: NonNullable<Data['effective']>): Form => {
  const b = e.baoTri ?? {};
  const f = { baoTri: { bat: b.bat === true, thongBao: b.thongBao ?? '', tu: toLocal(b.tu), den: toLocal(b.den), vanDangMo: b.vanDangMo === 'hoan' ? 'hoan' : 'chot', baoTruocPhut: b.baoTruocPhut ?? 0 } } as Form;
  for (const [k] of FEATURES) f[k] = { bat: e[k]?.bat !== false, thongBao: e[k]?.thongBao ?? '' };
  return f;
};
// the whole doc from the form: only keys the schema allows, empty text and times left out
const toDoc = (f: Form): Doc => {
  const b = f.baoTri;
  const baoTri: BaoTri = { bat: b.bat, vanDangMo: b.vanDangMo, baoTruocPhut: b.baoTruocPhut };
  if (b.thongBao.trim()) baoTri.thongBao = b.thongBao.trim();
  if (b.tu) baoTri.tu = fromLocal(b.tu);
  if (b.den) baoTri.den = fromLocal(b.den);
  const doc: Doc = { baoTri };
  for (const [k] of FEATURES) doc[k] = { bat: f[k].bat, ...(f[k].thongBao.trim() ? { thongBao: f[k].thongBao.trim() } : {}) };
  return doc;
};
const isStale = (e: unknown) => e instanceof ApiError && e.status === 409 && e.code === 'STALE';

function windowText(b: Form['baoTri']) {
  const tu = fromLocal(b.tu);
  const den = fromLocal(b.den);
  if (!tu && !den) return 'ngay khi lưu, tới khi tắt';
  return <>{tu ? <>từ <When v={tu} /></> : 'từ lúc lưu'} {den ? <>đến <When v={den} /></> : 'tới khi tắt'}</>;
}

export function LiveSwitchesPage() {
  const { can } = useAuth();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['live-switches'], queryFn: () => api.get<Data>('/api/admin/live/switches'), refetchInterval: 15_000 });
  const [draft, setDraft] = useState<Form | null>(null);
  // version the draft was built on, taken at the first edit (a refetch must not move it)
  const [base, setBase] = useState<number | null>(null);
  const [confirm, setConfirm] = useState(false);
  const [reason, setReason] = useState('');
  const [result, setResult] = useState<Command | null>(null);
  const m = useMutation({
    mutationFn: (f: Form) => api.put<{ command: Command }>('/api/admin/live/switches', {
      doc: toDoc(f), expect: base ?? q.data!.version, ...(reason.trim() ? { reason: reason.trim() } : {}),
    }),
    onSuccess: (r) => { setConfirm(false); setDraft(null); setBase(null); setReason(''); setResult(r.command); qc.invalidateQueries({ queryKey: ['live-switches'] }); },
    onError: (e) => { if (isStale(e)) setConfirm(false); },
  });
  const stale = isStale(m.error);
  const discard = () => { setDraft(null); setBase(null); m.reset(); qc.invalidateQueries({ queryKey: ['live-switches'] }); };

  if (q.isLoading) return <div className="page"><BlockSkeleton lines={10} /></div>;
  if (q.error || !q.data) return <div className="page"><ErrorState error={q.error} onRetry={() => q.refetch()} /></div>;
  const d = q.data;
  if (!d.effective) {
    return (
      <div className="page">
        <PageHeader title="Công tắc hệ thống" />
        <InlineNotification kind="error" lowContrast hideCloseButton title="Hệ A chưa trả cấu hình công tắc" subtitle="Không sửa được khi chưa biết cấu hình đang áp dụng. Thử lại sau khi hệ A chạy." />
      </div>
    );
  }
  const server = toForm(d.effective);
  const f = draft ?? server;
  const dirty = !!draft && JSON.stringify(draft) !== JSON.stringify(server);
  const editable = can('site.switch');
  const edit = (next: Form) => { setDraft(next); setBase(base ?? d.version); };
  const setB = (patch: Partial<Form['baoTri']>) => edit({ ...f, baoTri: { ...f.baoTri, ...patch } });
  const setF = (k: FeatureKey, patch: Partial<Form[FeatureKey]>) => edit({ ...f, [k]: { ...f[k], ...patch } });

  const b = f.baoTri;
  const badWindow = !!(b.tu && b.den && Date.parse(fromLocal(b.den)!) <= Date.parse(fromLocal(b.tu)!));
  const tooLong = b.thongBao.length > 300 || FEATURES.some(([k]) => f[k].thongBao.length > 300);
  const turningOn = b.bat && !server.baoTri.bat;
  const goingOff = FEATURES.filter(([k]) => server[k].bat && !f[k].bat);
  const save = () => { if (turningOn || goingOff.length) setConfirm(true); else m.mutate(f); };
  const scheduled = d.effective.baoTri?.bat && !d.maintenanceNow && d.effective.baoTri.tu && Date.parse(d.effective.baoTri.tu) > Date.now();

  return (
    <div className="page page--with-bar">
      <PageHeader title="Công tắc hệ thống"
        lead="Bảo trì toàn trang và bật / tắt từng chức năng cho mọi người chơi. Hệ A và hệ B áp dụng từ thao tác kế tiếp của người chơi, không cần khởi động lại." />
      <div className="row health">
        <span>Công tắc: {d.version === 0 ? <span className="muted">chưa đặt từ CMS, đang dùng mặc định</span> : <>phiên bản <span className="num">{d.version}</span>{d.at ? <span className="muted">, <When v={d.at} /></span> : null}</>}</span>
        {d.pending.length ? <span className="muted">{d.pending.length} lệnh đang gửi</span> : null}
      </div>
      {d.maintenanceNow ? (
        <InlineNotification kind="error" hideCloseButton title="Toàn trang đang bảo trì"
          subtitle={'Người chơi không đăng nhập, không cược, không nạp rút được. ' + (d.effective.baoTri?.den ? 'Tự hết lúc ' + formatVnDateTime(d.effective.baoTri.den) + '.' : 'Tắt bảo trì ở dưới để mở lại.')} />
      ) : null}
      {result ? (
        // the link sits under the notification: an InlineNotification may hold no interactive child and an
        // ActionableNotification traps focus (a click elsewhere on the page scrolls back to it)
        <div className="result-note">
          <InlineNotification kind={result.status === 'SUCCEEDED' ? 'success' : result.status === 'FAILED' ? 'error' : 'warning'} lowContrast onClose={() => setResult(null)}
            title={result.status === 'SUCCEEDED' ? 'Hệ A đã nhận công tắc mới' : result.status === 'FAILED' ? 'Hệ A từ chối công tắc mới' : 'Lệnh chưa xong'}
            subtitle={result.status === 'SUCCEEDED' ? '' : (result.lastError && HE_A_ERRORS[result.lastError]) || result.lastError || result.status} />
          <div className="row"><CommandStatus s={result.status} /><Link className="cell-link" to={'/commands/' + result.id}>Xem lệnh</Link></div>
        </div>
      ) : null}
      {stale ? (
        <ActionableNotification kind="warning" inline lowContrast hideCloseButton title="Công tắc vừa được người khác đổi"
          subtitle={`Bản bạn đang sửa dựa trên phiên bản ${base ?? '?'}, trên máy chủ đã là phiên bản ${d.version}. Chưa lưu gì. Bỏ bản nháp và tải lại, rồi sửa lại.`}
          actionButtonLabel="Bỏ bản nháp và tải lại" onActionButtonClick={discard} />
      ) : null}

      <section className="panel control">
        <div className="panel__head">
          <h2>Bảo trì toàn trang</h2>
          {d.maintenanceNow ? <Tag size="sm" type="red">Đang bảo trì</Tag> : scheduled ? <Tag size="sm" type="blue">Đã hẹn giờ</Tag> : <Tag size="sm" type="green">Đang mở</Tag>}
        </div>
        <fieldset className="control__group" disabled={!editable}>
          <Toggle id="bt-bat" labelText="Bảo trì toàn trang" labelA="Tắt" labelB="Bật" toggled={b.bat} onToggle={(v) => setB({ bat: v })} />
          <TextArea id="bt-tb" labelText="Thông báo người chơi thấy" rows={2} maxCount={300} enableCounter value={b.thongBao}
            placeholder="Để trống = câu mặc định" onChange={(e) => setB({ thongBao: e.target.value })} />
          <div className="row">
            <TextInput id="bt-tu" type="datetime-local" labelText="Bắt đầu (trống = ngay khi bật)" value={b.tu} onChange={(e) => setB({ tu: e.target.value })} />
            <TextInput id="bt-den" type="datetime-local" labelText="Kết thúc (trống = tới khi tắt)" value={b.den} invalid={badWindow} invalidText="Phải sau giờ bắt đầu"
              onChange={(e) => setB({ den: e.target.value })} />
          </div>
          <RadioButtonGroup legendText="Ván đang chạy khi bảo trì bắt đầu" name="bt-van" orientation="vertical" valueSelected={b.vanDangMo}
            onChange={(v) => setB({ vanDangMo: v === 'hoan' ? 'hoan' : 'chot' })}>
            <RadioButton id="bt-van-chot" value="chot" labelText="Ván đang chạy chốt bình thường" />
            <RadioButton id="bt-van-hoan" value="hoan" labelText="Hoàn tiền mọi ván còn cược" />
          </RadioButtonGroup>
          <NumberInput id="bt-truoc" label="Báo trước (phút, chữ chạy trong game khi có giờ bắt đầu; 0 = không báo)" min={0} max={1440} step={5}
            value={b.baoTruocPhut} onChange={(_e, { value }) => { const n = Math.floor(Number(value)); setB({ baoTruocPhut: Number.isFinite(n) ? Math.min(1440, Math.max(0, n)) : 0 }); }} />
        </fieldset>
      </section>

      <div className="control-grid section">
        {FEATURES.map(([k, label, what]) => (
          <section key={k} className="panel control">
            <div className="panel__head">
              <h2>{label}</h2>
              {server[k].bat ? <Tag size="sm" type="green">Đang mở</Tag> : <Tag size="sm" type="red">Đang tắt</Tag>}
            </div>
            <fieldset className="control__group" disabled={!editable}>
              <Toggle id={'ct-' + k} labelText={what} labelA="Tắt" labelB="Mở" toggled={f[k].bat} onToggle={(v) => setF(k, { bat: v })} />
              <TextArea id={'ct-tb-' + k} labelText="Câu từ chối khi tắt" rows={2} maxCount={300} enableCounter value={f[k].thongBao}
                placeholder="Để trống = câu mặc định" onChange={(e) => setF(k, { thongBao: e.target.value })} />
            </fieldset>
          </section>
        ))}
      </div>

      {dirty ? (
        <div className="actionbar" role="region" aria-label="Thay đổi chưa lưu">
          <span>Công tắc đã đổi, chưa lưu</span>
          <Button kind="ghost" size="md" onClick={discard}>Bỏ thay đổi</Button>
          <Button kind="primary" size="md" disabled={badWindow || tooLong || stale || m.isPending} onClick={save}>Lưu</Button>
        </div>
      ) : null}
      {dirty && !confirm ? <InlineError error={stale ? null : m.error} /> : null}
      <Modal open={confirm} danger modalHeading="Xác nhận đổi công tắc" primaryButtonText="Lưu và áp dụng" secondaryButtonText="Hủy"
        primaryButtonDisabled={m.isPending} onRequestClose={() => setConfirm(false)} onRequestSubmit={() => m.mutate(f)}>
        <div className="stack">
          <ul className="list">
            {turningOn ? (
              <li>Bật bảo trì toàn trang {windowText(b)}. {b.vanDangMo === 'hoan' ? 'Mọi ván còn cược được hoàn tiền lúc bắt đầu.' : 'Ván đang chạy chốt bình thường, không nhận cược mới.'}</li>
            ) : null}
            {goingOff.map(([k, label]) => <li key={k}>Tắt {label}: người chơi nhận câu "{f[k].thongBao.trim() || 'câu mặc định'}".</li>)}
          </ul>
          <p>Áp dụng cho mọi người chơi từ thao tác kế tiếp.</p>
          <TextArea id="ct-reason" labelText="Lý do (không bắt buộc)" rows={2} maxCount={200} enableCounter value={reason} onChange={(e) => setReason(e.target.value)} />
          <InlineError error={stale ? null : m.error} />
        </div>
      </Modal>
      {d.pending.length ? (
        <ul className="list section">
          {d.pending.map((p) => (
            <li key={p.id}><CommandStatus s={p.status} /><Link className="cell-link" to={'/commands/' + p.id}>Xem lệnh</Link><span className="muted"><When v={p.createdAt} /></span>
              {p.lastError ? <span className="text-error">{HE_A_ERRORS[p.lastError] ?? p.lastError}</span> : null}</li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
